import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { signupUser } from "../../redux/features/authSlice";

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signupLoading } = useSelector((state) => state.auth);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().required("Phone Number is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const { confirmPassword, ...rest } = values;
      const payload = {
        ...rest,
        phoneNumber: values.phone,
        displayName: `${values.firstName} ${values.lastName}`,
      };

      dispatch(signupUser(payload))
        .unwrap()
        .then(() => {
          toast.success("User registered successfully!", {
            position: "top-center",
            theme: "colored",
          });
          navigate("/");
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message, {
            position: "top-center",
            theme: "colored",
          });
        });
    },
  });

  return (
    <div>
      <main className="main pages">
        <div className="page-header breadcrumb-wrap">
          <div className="container">
            <div className="breadcrumb">
              <a rel="nofollow">
                <i className="fi-rs-home mr-5" />
                Home
              </a>
              <span>
                {" "}
                Register <span></span>
              </span>
            </div>
          </div>
        </div>
        <div className="page-content pt-50 pb-150">
          <div className="container">
            <div className="row">
              <div className="col-xl-8 col-lg-10 col-md-12 m-auto">
                <div className="row" style={{ alignItems: "center" }}>
                  <div className="col-lg-6 col-md-8">
                    <div className="login_wrap widget-taber-content background-white">
                      <div className="padding_eight_all bg-white">
                        <div className="heading_s1">
                          <h1 className="mb-5">Create an Account</h1>
                          <p className="mb-30">
                            Already have an account?
                            <Link to={"/login"}>
                              <a style={{ marginLeft: "4px" }}>Login</a>
                            </Link>
                          </p>
                        </div>
                        <form onSubmit={formik.handleSubmit}>
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                              marginBottom: 20,
                            }}
                          >
                            <div className="form-group">
                              <input
                                type="text"
                                name="firstName"
                                placeholder="First Name"
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${
                                  formik.touched.firstName &&
                                  formik.errors.firstName
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              {formik.touched.firstName &&
                              formik.errors.firstName ? (
                                <div className="invalid-feedback">
                                  {formik.errors.firstName}
                                </div>
                              ) : null}
                            </div>
                            <div className="form-group">
                              <input
                                type="text"
                                name="lastName"
                                placeholder="Last Name"
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${
                                  formik.touched.lastName &&
                                  formik.errors.lastName
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              {formik.touched.lastName &&
                              formik.errors.lastName ? (
                                <div className="invalid-feedback">
                                  {formik.errors.lastName}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="form-group">
                            <input
                              type="text"
                              name="email"
                              placeholder="Email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`form-control ${
                                formik.touched.email && formik.errors.email
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.email && formik.errors.email ? (
                              <div className="invalid-feedback">
                                {formik.errors.email}
                              </div>
                            ) : null}
                          </div>
                          <div className="form-group">
                            <input
                              type="text"
                              name="phone"
                              placeholder="Phone Number"
                              value={formik.values.phone}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`form-control ${
                                formik.touched.phone && formik.errors.phone
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.phone && formik.errors.phone ? (
                              <div className="invalid-feedback">
                                {formik.errors.phone}
                              </div>
                            ) : null}
                          </div>
                          <div className="form-group">
                            <input
                              type="password"
                              name="password"
                              placeholder="Password"
                              value={formik.values.password}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`form-control ${
                                formik.touched.password &&
                                formik.errors.password
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.password &&
                            formik.errors.password ? (
                              <div className="invalid-feedback">
                                {formik.errors.password}
                              </div>
                            ) : null}
                          </div>
                          <div className="form-group">
                            <input
                              type="password"
                              name="confirmPassword"
                              placeholder="Confirm Password"
                              value={formik.values.confirmPassword}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={`form-control ${
                                formik.touched.confirmPassword &&
                                formik.errors.confirmPassword
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.confirmPassword &&
                            formik.errors.confirmPassword ? (
                              <div className="invalid-feedback">
                                {formik.errors.confirmPassword}
                              </div>
                            ) : null}
                          </div>
                          <div className="login_footer form-group mb-30">
                            <div className="chek-form">
                              <div className="custome-checkbox">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  name="checkbox"
                                  id="exampleCheckbox12"
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="exampleCheckbox12"
                                >
                                  <span>I agree to Terms &amp; Policy.</span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div
                            className="form-group mb-30"
                            style={{ width: "100%" }}
                          >
                            <button
                              className="btn btn-fill-out btn-block hover-up font-weight-bold"
                              name="login"
                              style={{ width: "100%" }}
                              type="submit"
                            >
                              {signupLoading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                "SIGN UP"
                              )}
                            </button>
                          </div>
                          <p className="font-xs text-muted">
                            <strong>Note:</strong> Your personal data will be
                            used to support your experience throughout this
                            website, to manage access to your account, and for
                            other purposes described in our privacy policy
                          </p>
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 pr-30 d-none d-lg-block">
                    <img
                      className="border-radius-15"
                      src="assets/imgs/page/login.png"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
